import type { DashboardTransfer } from '@pos/types';

import { dashboardApi } from './apiClient';

export interface TransferView {
  id: string;
  referenceNo: string;
  createdByUserId?: string;
  createdByName?: string;
  receivedByUserId?: string;
  receivedByName?: string;
  fromBranchId: string;
  toBranchId: string;
  status: 'draft' | 'sent' | 'received' | 'cancelled';
  createdAt: number;
  receivedAt?: number;
  lines: Array<{ productId: string; qty: number }>;
}

interface TransferScope {
  branchId: string | null;
  allowedBranchIds?: string[];
}

export const transferReferenceNo = (id: string, createdAt: number): string => {
  const date = new Date(createdAt);
  const dateParts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Bangkok',
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
  })
    .formatToParts(date)
    .reduce<Record<string, string>>((parts, part) => {
      parts[part.type] = part.value;
      return parts;
    }, {});
  const suffix = id
    .replace(/^dashboard-transfer-/i, '')
    .replace(/[^a-z0-9]/gi, '');
  const shortId = suffix.slice(-6).toUpperCase().padStart(6, '0');
  return `TF-${dateParts.year}${dateParts.month}${dateParts.day}-${shortId}`;
};

const toTransfer = (transfer: DashboardTransfer): TransferView => {
  const createdAt = Date.parse(transfer.createdAt);
  return {
    id: transfer.id,
    referenceNo: transferReferenceNo(transfer.id, createdAt),
    createdByUserId: transfer.createdByUserId,
    createdByName: transfer.createdByName,
    receivedByUserId: transfer.receivedByUserId,
    receivedByName: transfer.receivedByName,
    fromBranchId: transfer.fromBranchId,
    toBranchId: transfer.toBranchId,
    status: transfer.status,
    createdAt,
    receivedAt: transfer.receivedAt
      ? Date.parse(transfer.receivedAt)
      : undefined,
    lines: transfer.lines.map((line) => ({
      productId: line.productId,
      qty: line.quantity,
    })),
  };
};

const branchIdsFor = async (scope: TransferScope): Promise<string[]> => {
  if (scope.branchId) return [scope.branchId];
  if (scope.allowedBranchIds?.length) return scope.allowedBranchIds;
  return (await dashboardApi.listBranches()).map((branch) => branch.id);
};

export async function listTransfers(
  scope: TransferScope,
): Promise<TransferView[]> {
  const pages = await Promise.all(
    (await branchIdsFor(scope)).map((branchId) =>
      dashboardApi.listTransfers({ branchId }),
    ),
  );
  const unique = new Map<string, TransferView>();
  for (const transfer of pages.flatMap((page) => page.transfers)) {
    unique.set(transfer.id, toTransfer(transfer));
  }
  return [...unique.values()].sort((a, b) => b.createdAt - a.createdAt);
}

export async function getTransfer(id: string): Promise<TransferView> {
  return toTransfer(await dashboardApi.getTransfer(id));
}

export async function createTransfer(input: {
  requestId: string;
  fromBranchId: string;
  toBranchId: string;
  lines: Array<{ productId: string; unitId: string; quantity: number }>;
}): Promise<TransferView> {
  return toTransfer(
    await dashboardApi.createTransfer({
      ...input,
    }),
  );
}

export async function receiveTransfer(
  id: string,
  requestId: string,
): Promise<TransferView> {
  return toTransfer(await dashboardApi.receiveTransfer(id, { requestId }));
}
